/**
 * issuesApi — raw-fetch client for the in-app error-collection surface
 * (ADR-0121, feature plan `docs/plans/error_collection.md`).
 *
 * This is SHELL code (lives under apps/cockpit/src/lib), so the raw
 * `fetch` here is conformant with `ken/no-direct-fetch` — the same
 * scoping that lets install401Handler.ts and the approvals page call
 * fetch directly. Module code must still use @ken/api-client.
 *
 * The API contract is FROZEN (built verbatim by the backend agent):
 *   POST /api/shell/issues            → { id, deduped, occurrences }
 *   GET  /api/shell/issues?status=    → { issues: [...] }        (admin.issues.view)
 *   GET  /api/shell/issues/{id}       → issue + { events: [...] } (admin.issues.view)
 *   POST /api/shell/issues/{id}/events → { ok: true }            (admin.issues.manage)
 *
 * `createIssue` is the fire-and-forget auto/manual reporter. It MUST be
 * absolutely silent on any failure: no throw, no console output, no
 * retry (the amplification guard — a reporter that logs on failure can
 * turn one crash into an infinite console→capture→report storm). Every
 * other call throws on a non-2xx so the admin page can surface a clear
 * error state.
 */

const BASE = "/api/shell/issues";

// ---------------------------------------------------------------------------
// Types — mirror the frozen POST/GET shapes.
// ---------------------------------------------------------------------------

export interface Pin {
  /** 0..1 fraction of viewport width. */
  x: number;
  /** 0..1 fraction of viewport height. */
  y: number;
  vw: number;
  vh: number;
  selector: string;
  label: string;
}

export type IssueKind = "auto" | "manual";
export type IssueType = "ui" | "data" | "crash" | "idea";
export type IssueSeverity = "low" | "med" | "high";
export type IssueStatus = "open" | "confirmed" | "fixed" | "closed";

export interface NewIssuePayload {
  kind: IssueKind;
  issue_type?: IssueType;
  severity?: IssueSeverity;
  title: string;
  did?: string;
  expected?: string;
  saw?: string;
  route?: string;
  url_params?: string;
  app_commit?: string;
  user_agent?: string;
  console_tail?: string;
  stack?: string;
  pins?: Pin[];
}

export interface CreateIssueResult {
  id: string;
  deduped: boolean;
  occurrences: number;
}

export interface IssueEvent {
  ts: string;
  actor_user_id: string | null;
  actor_email?: string;
  action: string;
  value: string;
}

export interface Issue {
  id: string;
  reporter_user_id?: string | null;
  kind: IssueKind;
  issue_type: IssueType;
  severity: IssueSeverity;
  status: IssueStatus;
  title: string;
  did: string;
  expected: string;
  saw: string;
  route: string;
  url_params: string;
  app_commit: string;
  user_agent: string;
  console_tail: string;
  stack: string;
  pins: Pin[];
  occurrences: number;
  last_seen_at: string;
  created_at: string;
  updated_at: string;
}

export interface IssueDetail extends Issue {
  events: IssueEvent[];
}

// ---------------------------------------------------------------------------
// createIssue — the silent fire-and-forget reporter.
// ---------------------------------------------------------------------------

/**
 * File an issue (auto crash report or a manual pin report). Resolves to
 * the server result on success, or `undefined` on ANY failure — network
 * error, non-2xx, or malformed body. It never throws and never logs: the
 * failure path is `.catch(() => undefined)` with nothing else, so a
 * reporter failure can never amplify into more console output or more
 * requests.
 */
export function createIssue(
  payload: NewIssuePayload,
): Promise<CreateIssueResult | undefined> {
  return fetch(BASE, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  })
    .then((res) => (res.ok ? (res.json() as Promise<CreateIssueResult>) : undefined))
    .catch(() => undefined);
}

// ---------------------------------------------------------------------------
// Admin reads/writes — throw on failure so the page shows an error state.
// ---------------------------------------------------------------------------

export async function listIssues(status?: string): Promise<{ issues: Issue[] }> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : "";
  const res = await fetch(`${BASE}${qs}`, { credentials: "include" });
  if (!res.ok) throw new Error(`issues ${res.status}`);
  return res.json() as Promise<{ issues: Issue[] }>;
}

export async function getIssue(id: string): Promise<IssueDetail> {
  const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`issue ${res.status}`);
  return res.json() as Promise<IssueDetail>;
}

export async function addIssueEvent(
  id: string,
  action: "status" | "comment",
  value: string,
): Promise<{ ok: true }> {
  const res = await fetch(`${BASE}/${encodeURIComponent(id)}/events`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action, value }),
  });
  if (!res.ok) throw new Error(`event ${res.status}`);
  return res.json() as Promise<{ ok: true }>;
}
